import React, { useEffect, useRef, useState } from 'react';
import MemberLogoConvergence from '../components/MemberLogoConvergence';
import { members } from '../data/members';

const stats = [
  { value: 20, suffix: '+', label: 'Member Organizations' },
  { value: 3, suffix: '+', label: 'Years of Collaboration' },
  { value: 25, suffix: '+', label: 'Tanzania Regions Reached' },
  { value: 100, suffix: '%', label: 'Commitment to Coordination' },
];

const categories = [
  {
    icon: '🌐',
    title: 'International NGOs',
    desc: 'Global organizations operating humanitarian and disaster management programs in Tanzania are welcome as full members with voting rights on the Executive Committee.',
    examples:
      'World Vision · CARE · CRS · DRC · MSF · Save the Children · Plan International',
  },
  {
    icon: '🇹🇿',
    title: 'National NGOs',
    desc: 'Tanzanian-registered organizations actively engaged in disaster response, preparedness, or resilience building at local, regional, or national level.',
    examples:
      'RAPID-Tanzania · Tanzania Red Cross · KOVA Foundation · Art in Tanzania · Kituja Foundation · HUDEFO',
  },
  {
    icon: '✅',
    title: 'Become a Member',
    desc: 'Submit a formal membership request with proof of active engagement in humanitarian or disaster management work. Pay the membership fee of TZS 50,000 and monthly contribution of TZS 50,000.',
    cta: 'Apply for Membership',
  },
];

function useInView(threshold = 0.2) {
  const [inView, setInView] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const element = ref.current;
    if (!element || inView) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [inView, threshold]);

  return [ref, inView];
}

function useCounter(target, duration, start) {
  const [count, setCount] = useState(0);
  const rafRef = useRef(0);

  useEffect(() => {
    if (!start) return undefined;

    let startTime = null;
    const tick = (time) => {
      if (!startTime) startTime = time;
      const progress = Math.min((time - startTime) / duration, 1);
      setCount(Math.floor(progress * target));
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [duration, start, target]);

  return count;
}

function StatCard({ stat, index, visible }) {
  const number = useCounter(stat.value, 1400, visible);

  return (
    <article
      className="rounded-2xl border border-white/10 bg-white/5 p-8 text-center backdrop-blur-sm"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(24px)',
        transition: 'opacity 600ms ease, transform 600ms ease',
        transitionDelay: `${index * 100}ms`,
      }}
    >
      <p className="text-5xl font-black text-[#00E5CC]">
        {number}
        {visible ? stat.suffix : ''}
      </p>
      <p className="mt-2 text-xs uppercase tracking-widest text-slate-400">{stat.label}</p>
    </article>
  );
}

function MemberCard({ item, index, visible }) {
  const [logoFailed, setLogoFailed] = useState(false);
  const initials = item.name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
  const isInternational = item.type === 'international';
  const showLogo = item.logo && !logoFailed;

  return (
    <article
      className="group cursor-default rounded-xl border border-white/10 bg-white/5 p-5 transition-all duration-300 hover:border-[#00E5CC]/40 hover:bg-[#00E5CC]/5 hover:shadow-[0_0_20px_rgba(0,229,204,0.08)]"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'scale(1)' : 'scale(0.95)',
        transitionDelay: `${index * 30}ms`,
      }}
    >
      <div className="flex items-start justify-between gap-3">
        <div
          className={`flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-full border border-[#00E5CC]/20 ${
            showLogo ? 'bg-white p-1.5' : 'bg-gradient-to-br from-[#00E5CC]/20 to-[#00BFA5]/10 text-sm font-bold text-[#00E5CC]'
          }`}
        >
          {showLogo ? (
            <img
              src={`/assets/${encodeURIComponent(item.logo)}`}
              alt=""
              className="h-full w-full object-contain"
              onError={() => setLogoFailed(true)}
            />
          ) : (
            initials
          )}
        </div>
        <span
          className={`rounded-full px-2 py-0.5 text-[10px] ${
            isInternational
              ? 'border border-blue-500/20 bg-blue-500/10 text-blue-400'
              : 'border border-emerald-500/20 bg-emerald-500/10 text-emerald-400'
          }`}
        >
          {isInternational ? 'International' : 'National'}
        </span>
      </div>
      <h3 className="mt-3 text-sm font-semibold leading-snug text-white">{item.name}</h3>
      <p className="mt-1 text-[10px] uppercase tracking-wide text-slate-500">{item.focus}</p>
      <p className="mt-3 border-t border-white/5 pt-3 text-[10px] text-slate-600">
        {item.status || 'Member since 2024'}
      </p>
    </article>
  );
}

export default function MembersPage({ onNavigate }) {
  const [statsRef, statsInView] = useInView(0.25);
  const [gridRef, gridInView] = useInView(0.12);
  const [query, setQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');

  const filteredMembers = members.filter((member) => {
    const matchesQuery = member.name.toLowerCase().includes(query.trim().toLowerCase());
    const matchesType = activeFilter === 'all' ? true : member.type === activeFilter;
    return matchesQuery && matchesType;
  });

  return (
    <div className="min-h-screen bg-[#0B1629] text-white">
      <main>
        <section className="relative overflow-hidden px-6 pb-24 pt-36">
          <div className="pointer-events-none absolute left-1/2 top-1/2 h-[350px] w-[700px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#00E5CC]/5 blur-3xl" />
          <div className="pointer-events-none absolute right-16 top-16 h-56 w-56 rounded-full bg-teal-500/5 blur-2xl" />

          <div className="mx-auto max-w-4xl text-center">
            <span className="mb-6 inline-block rounded-full border border-[#00E5CC]/30 px-4 py-1.5 text-xs uppercase tracking-[0.25em] text-[#00E5CC]">
              MEMBERS
            </span>
            <h1 className="mb-6 text-5xl font-black leading-tight md:text-7xl">
              Our Network of <span className="text-[#00E5CC]">Change Makers</span>
            </h1>
            <p className="mx-auto max-w-2xl text-base leading-relaxed text-slate-400">
              DINGONET brings together over 20 national and international organizations united by one mission -
              coordinated, timely, and effective humanitarian action across all regions of Tanzania.
            </p>
          </div>
        </section>

        <section ref={statsRef} className="mx-auto max-w-5xl px-6 pb-24">
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            {stats.map((stat, index) => (
              <StatCard key={stat.label} stat={stat} index={index} visible={statsInView} />
            ))}
          </div>
        </section>

        <section className="mx-auto max-w-6xl px-6 pb-24">
          <div className="mx-auto mb-10 max-w-3xl text-center">
            <h2 className="text-4xl font-black md:text-5xl">
              Who Can <span className="text-[#00E5CC]">Join?</span>
            </h2>
            <p className="mx-auto mt-4 max-w-3xl text-slate-400">
              DINGONET membership is open to all organizations with proven engagement in humanitarian and disaster
              management activities in Tanzania.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {categories.map((card) => (
              <article
                key={card.title}
                className="rounded-2xl border border-white/10 bg-gradient-to-br from-slate-800/80 to-slate-900/80 p-8 transition-all duration-500 hover:border-[#00E5CC]/40 hover:shadow-[0_0_40px_rgba(0,229,204,0.12)]"
              >
                <div className="mb-4 text-4xl">{card.icon}</div>
                <h3 className="mb-3 text-xl font-bold text-white">{card.title}</h3>
                <p className="mb-4 text-sm leading-relaxed text-slate-400">{card.desc}</p>
                {card.examples ? <p className="text-xs italic text-[#00E5CC]/80">{card.examples}</p> : null}
                {card.cta ? (
                  <button
                    type="button"
                    onClick={() => onNavigate?.('contact')}
                    className="mt-4 w-full rounded-full bg-[#00E5CC] px-6 py-2.5 font-bold text-[#0B1629] transition-colors hover:bg-[#00BFA5]"
                  >
                    {card.cta}
                  </button>
                ) : null}
              </article>
            ))}
          </div>
        </section>

        <section className="mx-auto max-w-6xl px-6 pb-28">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="text-4xl font-black md:text-5xl">
              Current <span className="text-[#00E5CC]">Member Organizations</span>
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-slate-400">
              A powerful coalition of national and international partners - from global relief giants to grassroots
              Tanzanian NGOs.
            </p>
          </div>

          <div className="mb-10 mt-10">
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search members..."
              className="mx-auto block w-full max-w-md rounded-full border border-white/10 bg-white/5 px-5 py-3 text-white outline-none placeholder:text-slate-500 focus:border-[#00E5CC]/40"
            />
            <div className="mt-4 flex justify-center gap-2">
              {[
                { key: 'all', label: 'All' },
                { key: 'international', label: 'International NGOs' },
                { key: 'national', label: 'National NGOs' },
              ].map((tab) => {
                const isActive = activeFilter === tab.key;
                return (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setActiveFilter(tab.key)}
                    className={`rounded-full px-4 py-1.5 text-xs transition ${
                      isActive
                        ? 'border border-[#00E5CC]/30 bg-[#00E5CC]/15 text-[#00E5CC]'
                        : 'border border-white/10 bg-white/5 text-slate-400'
                    }`}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mb-8 overflow-hidden rounded-2xl border border-white/10">
            <div className="relative h-56">
              <img
                src="/assets/kariakoo-pic1.jpg"
                alt="DINGONET member organizations in action"
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B1629] via-[#0B1629]/40 to-transparent" />
              <div className="absolute bottom-5 left-5">
                <p className="text-xs uppercase tracking-wider text-[#00E5CC]">Members in Action</p>
                <h3 className="mt-1 text-xl font-bold text-white">Coordination that reaches every region</h3>
              </div>
            </div>
          </div>

          <div ref={gridRef} className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {filteredMembers.map((member, index) => (
              <MemberCard key={member.name} item={member} index={index} visible={gridInView} />
            ))}
          </div>

          <MemberLogoConvergence members={members} className="mt-12" />
        </section>

        <section className="mx-auto max-w-5xl px-6 pb-28">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-4xl font-black md:text-5xl">
              Rights &amp; <span className="text-[#00E5CC]">Responsibilities</span>
            </h2>
            <p className="mt-4 text-slate-400">
              Every member contributes to and benefits from a coordinated humanitarian network built on accountability,
              collaboration, and shared impact.
            </p>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-8 md:grid-cols-2">
            <article className="rounded-2xl border border-white/10 bg-gradient-to-br from-slate-800/80 to-slate-900/80 p-8">
              <h3 className="mb-4 text-2xl font-bold text-white">⭐ Member Rights</h3>
              {[
                'Vote and be elected to the Executive Committee',
                'Access to joint inter-agency assessments and reports',
                'Participate in all network meetings and activities',
                "Access DINGONET's 3Ws coordination database",
                'Benefit from joint training and simulation exercises',
                'Collaborate on consortium funding proposals',
                'Use DINGONET membership for credibility and visibility',
              ].map((item) => (
                <div
                  key={item}
                  className="flex items-start gap-3 border-b border-white/5 py-2 text-sm text-slate-300 last:border-0"
                >
                  <span className="text-[#00E5CC]">✓</span>
                  <span>{item}</span>
                </div>
              ))}
            </article>

            <article className="rounded-2xl border border-white/10 bg-gradient-to-br from-slate-800/80 to-slate-900/80 p-8">
              <h3 className="mb-4 text-2xl font-bold text-white">📌 Member Responsibilities</h3>
              {[
                'Share information and experience through agreed channels',
                'Attend bimonthly network meetings and activities',
                'Participate in joint assessments, drills, and training',
                'Pay TZS 50,000 membership fee + TZS 50,000/month contribution',
                "Uphold DINGONET's code of conduct and humanitarian principles",
                'Contribute to collective advocacy and policy engagement',
                'Support coordination and avoid duplication of efforts',
              ].map((item) => (
                <div
                  key={item}
                  className="flex items-start gap-3 border-b border-white/5 py-2 text-sm text-slate-300 last:border-0"
                >
                  <span className="text-[#00E5CC]">→</span>
                  <span>{item}</span>
                </div>
              ))}
            </article>
          </div>
        </section>

        <section className="mx-auto max-w-5xl px-6 pb-28">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="text-4xl font-black md:text-5xl">
              How We <span className="text-[#00E5CC]">Govern</span>
            </h2>
            <p className="mt-4 text-slate-400">
              DINGONET is led by an elected Executive Committee of six representatives - ensuring balanced national and
              international NGO leadership.
            </p>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3">
            {[
              {
                icon: '🏛️',
                title: 'Executive Committee',
                desc: '6 elected members: 3 from international NGOs, 2 from national NGOs, and 1 NGO secretariat representative. Serving one-year tenures.',
              },
              {
                icon: '📅',
                title: 'Regular Meetings',
                desc: 'Bimonthly partner meetings for strategic decisions, updates and networking. Executive Committee meets as needed for specific operational or project requirements.',
              },
              {
                icon: '🏢',
                title: 'Secretariat',
                desc: 'Day-to-day coordination and administration is hosted by RAPID-Tanzania in Dar es Salaam - managing communications, documentation, and government liaison.',
              },
            ].map((tile) => (
              <article
                key={tile.title}
                className="rounded-2xl border border-white/10 bg-white/5 p-8 text-center transition-all duration-300 hover:border-[#00E5CC]/30"
              >
                <div className="mb-4 text-4xl">{tile.icon}</div>
                <h3 className="mb-3 text-lg font-bold text-white">{tile.title}</h3>
                <p className="text-sm leading-relaxed text-slate-400">{tile.desc}</p>
              </article>
            ))}
          </div>
        </section>

        <section id="join-dingonet" className="mx-auto max-w-3xl scroll-mt-28 px-6 pb-24 text-center">
          <div className="rounded-3xl border border-[#00E5CC]/20 bg-gradient-to-br from-[#00E5CC]/10 to-[#00BFA5]/5 p-12">
            <div className="mb-4 text-5xl">🤝</div>
            <h2 className="text-3xl font-black text-white">Ready to Join DINGONET?</h2>
            <div className="my-8 flex flex-col justify-center gap-4 sm:flex-row sm:gap-8">
              {[
                { value: 'TZS 50,000', label: 'One-Time Membership Fee' },
                { value: 'TZS 50,000', label: 'Monthly Contribution' },
              ].map((fee) => (
                <div key={fee.label} className="rounded-xl border border-white/10 bg-white/5 px-8 py-5">
                  <p className="text-2xl font-black text-[#00E5CC]">{fee.value}</p>
                  <p className="mt-1 text-xs uppercase tracking-wider text-slate-400">{fee.label}</p>
                </div>
              ))}
            </div>
            <p className="mb-8 text-slate-400">
              Membership is open to all organizations with proven engagement in humanitarian and disaster management
              activities in Tanzania. Submit a formal request with proof of active engagement.
            </p>
            <div className="flex flex-col justify-center gap-3 sm:flex-row">
              <button
                type="button"
                onClick={() => onNavigate?.('contact')}
                className="rounded-full bg-[#00E5CC] px-8 py-3 font-bold text-[#0B1629] transition-colors hover:bg-[#00BFA5]"
              >
                Apply for Membership
              </button>
              <button
                type="button"
                onClick={() => onNavigate?.('contact', 'get-in-touch')}
                className="rounded-full border border-white/20 px-8 py-3 text-white transition-colors hover:border-white/40"
              >
                Contact Secretariat
              </button>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-white/5 py-8 text-center text-sm text-slate-500">
        <p>
          DINGONET Secretariat (Hosted by RAPID-Tanzania) · P.O. Box 11189, Dar es Salaam ·
          info@dingonet.org · Executive Director 0757724717 · Lead of Advocacy and Communication 0744388179
        </p>
        <p className="mt-2">Reg. No. 8894 · Registered January 2026</p>
      </footer>
    </div>
  );
}
